import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../services/api';
import {
  Phone, RefreshCw, Loader2, AlertCircle, ChevronRight, ChevronLeft,
  ChevronUp, ChevronDown, ExternalLink, MessageSquare,
  ClipboardList, FileCheck, Send, Printer, ShoppingCart,
  FileText, CheckSquare, CheckCircle, X, Clock, CalendarDays,
  Package, Users2, User, Mail, Copy, PhoneCall, FolderKanban,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { AddCallToCrmModal } from './AddCallToCrmModal';
import { useZohoSession } from '../contexts/ZohoSessionContext';
import { ContactNotesTasksModal } from './ContactNotesTasksModal';
import { EightXEightCallsModal, EIGHT_X_EIGHT_LIME, EIGHT_X_EIGHT_DARK } from './EightXEightCallsBar';
import TaskProjectLinkModal from './TaskProjectLinkModal';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';
import { useV3Shell } from '../v3/V3ShellContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CALLS_SAFETY_NET_MS  = 45 * 60 * 1000;
const PI_SAFETY_NET_MS     =  5 * 60 * 1000;
const TASKS_SAFETY_NET_MS  = 10 * 60 * 1000;
const STOCK_SAFETY_NET_MS  =  90 * 1000;

const CALLS_CHANGED_EVENT      = 'c30:calls-changed';
const PI_COUNTS_CHANGED_EVENT  = 'c30:pi-counts-changed';
const TASKS_CHANGED_EVENT      = 'c30:tasks-changed';
const STOCK_CHANGED_EVENT      = 'c30:stock-changed';
const QUOTES_CHANGED_EVENT     = 'c30:quotes-changed';

let _callsCache = null;
let _piCache    = null;
let _tasksCache = null;

const DEFAULT_NOTIF_SETTINGS = {
  enabled: true,
  branch_enabled: true,
  notifications: [
    { type: 'calls',        enabled: true, order: 0 },
    { type: 'tasks',        enabled: true, order: 1 },
    { type: 'pi_counts',    enabled: true, order: 2 },
    { type: 'stock',        enabled: true, order: 3 },
    { type: 'orders_quotes',enabled: true, order: 4 },
  ],
};

export const NotificationBars = ({ user }) => {
  const {
    isLinked,
    isSalesTeam,
    isAdminGroup,
    isPrimarySuperAdmin,
    loading: sessionLoading,
  } = useZohoSession();

  // Home page background theme is a personal per-user preference (Settings > General), not an admin feature flag.
  const v3Shell = useV3Shell();
  const darkGlassEnabled = v3Shell ? v3Shell.theme === 'dark' : user?.home_theme === 'dark';

  const [calls, setCalls]               = useState(() => _callsCache?.data || []);
  const [callsLoading, setCallsLoading] = useState(() => !_callsCache);
  const [callsRefreshing, setCallsRefreshing] = useState(false);
  const [activeCall, setActiveCall]     = useState(null);
  const sseRef = useRef(null);

  const fetchCalls = useCallback(async () => {
    if (!isLinked) {
      _callsCache = { data: [], at: Date.now() };
      setCalls([]); setCallsLoading(false);
      return;
    }
    try {
      const res = await api.get(`${API}/zoho/my-scheduled-calls`);
      const list = res.data?.calls || [];
      _callsCache = { data: list, at: Date.now() };
      setCalls(list);
    } catch (_) {}
    finally { setCallsLoading(false); }
  }, [isLinked]);

  const manualRefreshCalls = useCallback(async () => {
    setCallsRefreshing(true);
    await fetchCalls();
    setCallsRefreshing(false);
  }, [fetchCalls]);

  useEffect(() => {
    if (sessionLoading || !isLinked) return;
    let cancelled = false;
    let esRef = null;
    let retryTimer = null;
    const connect = async () => {
      if (cancelled) return;
      try {
        const res = await api.get(`${API}/calls/sse-token`);
        if (cancelled) return;
        const es = new EventSource(`${API}/calls/sse?token=${encodeURIComponent(res.data.token)}`);
        esRef = es;
        sseRef.current = es;
        es.addEventListener('calls_changed', () => {
          fetchCalls();
          window.dispatchEvent(new Event(CALLS_CHANGED_EVENT));
        });
        es.onerror = () => {
          es.close();
          if (!cancelled) retryTimer = setTimeout(connect, 5000);
        };
        retryTimer = setTimeout(() => { es.close(); if (!cancelled) connect(); }, 80000);
      } catch { if (!cancelled) retryTimer = setTimeout(connect, 10000); }
    };
    connect();
    return () => { cancelled = true; clearTimeout(retryTimer); esRef?.close(); sseRef.current = null; };
  }, [fetchCalls, isLinked, sessionLoading]);

  useEffect(() => {
    if (sessionLoading) return;
    if (!isLinked) { setCalls([]); setCallsLoading(false); return; }
    const age = _callsCache ? Date.now() - _callsCache.at : Infinity;
    if (!_callsCache || age > CALLS_SAFETY_NET_MS) fetchCalls();
    else setCallsLoading(false);
    const id = setInterval(() => { if (document.visibilityState === 'visible') fetchCalls(); }, CALLS_SAFETY_NET_MS);
    const h = () => fetchCalls();
    window.addEventListener(CALLS_CHANGED_EVENT, h);
    return () => { clearInterval(id); window.removeEventListener(CALLS_CHANGED_EVENT, h); };
  }, [fetchCalls, isLinked, sessionLoading]);

  const handleCallCompleted = (callId) => {
    setCalls(prev => { const n = prev.filter(c => c.id !== callId); _callsCache = { data: n, at: Date.now() }; return n; });
    setActiveCall(null);
    window.dispatchEvent(new Event(CALLS_CHANGED_EVENT));
  };
  const handleCallDeleted = (callId) => {
    setCalls(prev => { const n = prev.filter(c => c.id !== callId); _callsCache = { data: n, at: Date.now() }; return n; });
    setActiveCall(null);
    window.dispatchEvent(new Event(CALLS_CHANGED_EVENT));
  };

  const [piCounts, setPiCounts]       = useState(() => _piCache?.data || []);
  const [piLoading, setPiLoading]     = useState(() => !_piCache);

  const fetchPi = useCallback(async () => {
    try {
      const res = await api.get(`${API}/pi-counts/action-required-summary`);
      const list = res.data?.counts || [];
      _piCache = { data: list, at: Date.now() };
      setPiCounts(list);
    } catch (_) {}
    finally { setPiLoading(false); }
  }, []);

  useEffect(() => {
    if (sessionLoading) return;
    if (!isAdminGroup) { _piCache = { data: [], at: Date.now() }; setPiCounts([]); setPiLoading(false); return; }
    const age = _piCache ? Date.now() - _piCache.at : Infinity;
    if (!_piCache || age > PI_SAFETY_NET_MS) fetchPi();
    else setPiLoading(false);
    const id = setInterval(() => { if (document.visibilityState === 'visible') fetchPi(); }, PI_SAFETY_NET_MS);
    const h = () => fetchPi();
    window.addEventListener(PI_COUNTS_CHANGED_EVENT, h);
    return () => { clearInterval(id); window.removeEventListener(PI_COUNTS_CHANGED_EVENT, h); };
  }, [fetchPi, isAdminGroup, sessionLoading]);

  const [tasks, setTasks]           = useState(() => _tasksCache?.data || []);
  const [tasksLoading, setTasksLoading] = useState(() => !_tasksCache);

  const fetchTasks = useCallback(async () => {
    if (!isLinked) {
      _tasksCache = { data: [], at: Date.now() };
      setTasks([]); setTasksLoading(false);
      return;
    }
    try {
      const res = await api.get(`${API}/zoho/my-tasks`);
      const list = res.data?.tasks || [];
      _tasksCache = { data: list, at: Date.now() };
      setTasks(list);
    } catch (_) {}
    finally { setTasksLoading(false); }
  }, [isLinked]);

  useEffect(() => {
    if (sessionLoading) return;
    if (!isLinked) { setTasks([]); setTasksLoading(false); return; }
    const age = _tasksCache ? Date.now() - _tasksCache.at : Infinity;
    if (!_tasksCache || age > TASKS_SAFETY_NET_MS) fetchTasks();
    else setTasksLoading(false);
    const id = setInterval(() => { if (document.visibilityState === 'visible') fetchTasks(); }, TASKS_SAFETY_NET_MS);
    const h = () => fetchTasks();
    window.addEventListener(TASKS_CHANGED_EVENT, h);
    return () => { clearInterval(id); window.removeEventListener(TASKS_CHANGED_EVENT, h); };
  }, [fetchTasks, isLinked, sessionLoading]);

  const handleTaskComplete = (taskId) => {
    setTasks(prev => {
      const n = prev.filter(t => t.id !== taskId);
      _tasksCache = { data: n, at: Date.now() };
      return n;
    });
  };

  const handleTaskSaved = useCallback((taskId) => {
    setTasks(prev => {
      const n = prev.filter(t => t.id !== taskId);
      _tasksCache = { data: n, at: Date.now() };
      return n;
    });
    window.dispatchEvent(new Event(TASKS_CHANGED_EVENT));
  }, []);

  const [stockSummary, setStockSummary]     = useState(null);
  const [stockLoading, setStockLoading]     = useState(true);
  const [quotesData, setQuotesData]         = useState(null);
  const [notifSettings, setNotifSettings]   = useState(DEFAULT_NOTIF_SETTINGS);
  const [settingsLoading, setSettingsLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { setStockLoading(false); setSettingsLoading(false); return; }
    try {
      const r = await api.get(`${API}/dashboard/init`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { quotes, stock, settings } = r.data || {};
      if (quotes)   setQuotesData(quotes);
      if (stock)    setStockSummary(stock);
      if (settings && (Array.isArray(settings.notifications) || typeof settings.enabled !== 'undefined'))
        setNotifSettings(settings);
    } catch { }
    finally { setStockLoading(false); setSettingsLoading(false); }
  }, []);

  useEffect(() => {
    if (sessionLoading) return;
    fetchDashboard();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') fetchDashboard();
    }, STOCK_SAFETY_NET_MS);
    const onStock  = () => fetchDashboard();
    const onQuotes = () => fetchDashboard();
    window.addEventListener(STOCK_CHANGED_EVENT,  onStock);
    window.addEventListener(QUOTES_CHANGED_EVENT, onQuotes);
    return () => {
      clearInterval(id);
      window.removeEventListener(STOCK_CHANGED_EVENT,  onStock);
      window.removeEventListener(QUOTES_CHANGED_EVENT, onQuotes);
    };
  }, [sessionLoading, fetchDashboard]);

  const isTypeEnabled = (type) => {
    if (!notifSettings.enabled || !notifSettings.branch_enabled) return false;
    const n = (notifSettings.notifications || []).find(n => n.type === type);
    return n ? (n.enabled !== false) : true;
  };

  const showCalls  = !settingsLoading && isTypeEnabled('calls')         && isLinked        && !sessionLoading;
  const showPi     = !settingsLoading && isTypeEnabled('pi_counts')     && isAdminGroup    && !sessionLoading;
  const showTasks  = !settingsLoading && isTypeEnabled('tasks')         && isLinked        && !sessionLoading;
  const showStock  = !settingsLoading && isTypeEnabled('stock')         && !stockLoading;
  const showOrders = !settingsLoading && isTypeEnabled('orders_quotes') && !sessionLoading;

  if (!showCalls && !showPi && !showTasks && !showStock && !showOrders) return null;
  if (settingsLoading || sessionLoading) return null;

  let defaultFeed = 'calls';
  if (!showCalls && showTasks) defaultFeed = 'tasks';
  else if (!showCalls && !showTasks && showPi) defaultFeed = 'pi';
  else if (!showCalls && !showTasks && !showPi && showStock) defaultFeed = 'stock';

  return (
    <>
      <UnifiedNotificationBar
        defaultFeed={defaultFeed}
        showCalls={showCalls}
        showPi={showPi}
        showTasks={showTasks}
        showStock={showStock}
        showOrders={showOrders}
        calls={calls}
        piCounts={piCounts}
        tasks={tasks}
        stockSummary={stockSummary}
        quotesCount={quotesData?.count || 0}
        quotesBreakdown={quotesData}
        callsLoading={callsLoading}
        piLoading={piLoading}
        tasksLoading={tasksLoading}
        stockLoading={stockLoading}
        callsRefreshing={callsRefreshing}
        manualRefreshCalls={manualRefreshCalls}
        onCallClick={setActiveCall}
        activeCall={activeCall}
        onCloseCall={() => setActiveCall(null)}
        onCallCompleted={handleCallCompleted}
        onCallDeleted={handleCallDeleted}
        onTaskComplete={handleTaskComplete}
        onTaskSaved={handleTaskSaved}
        notifSettings={notifSettings}
        darkGlassEnabled={darkGlassEnabled}
      />
      <AddCallToCrmModal
        isOpen={!!activeCall}
        onClose={() => setActiveCall(null)}
        scheduledCall={activeCall}
        onCompleted={handleCallCompleted}
        onDeleted={handleCallDeleted}
      />
    </>
  );
};

const OverdueTasksModal = ({ tasks, onClose, onTaskClick }) => {
  const fmtDate = (d) => {
    if (!d) return '';
    try {
      const dt = new Date(d);
      return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getFullYear()).slice(-2)}`;
    } catch { return ''; }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4" onClick={onClose} data-testid="overdue-tasks-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl border border-red-200 w-full max-w-md max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="overdue-tasks-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-red-100">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-red-600 flex items-center justify-center">
              <CheckSquare className="w-3.5 h-3.5 text-white" />
            </div>
            <h3 className="font-bold text-gray-800">Overdue Tasks</h3>
            <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full bg-red-100 text-red-700 text-xs font-bold">{tasks.length}</span>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" data-testid="overdue-modal-close">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {tasks.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No overdue tasks</p>
          ) : tasks.map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                onTaskClick(t);
                onClose();
              }}
              className="w-full flex items-start justify-between gap-3 px-3 py-2.5 rounded-xl border border-red-100 bg-red-50 hover:bg-red-100 hover:border-red-200 transition-colors text-left"
              data-testid={`overdue-task-item-${t.id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm text-gray-900 truncate">
                  {t.account_name || (t.account?.module?.api_name === 'Accounts' ? t.account?.name : null) || '—'}
                </p>
                <p className="text-xs text-gray-600 truncate mt-0.5">{t.subject}</p>
                <p className="text-[11px] text-red-600 flex items-center gap-1 mt-0.5">
                  <CalendarDays className="w-3 h-3" /> Due {fmtDate(t.due_date)}
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 mt-1" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const TaskCompleteBtn = ({ taskId, onComplete }) => {
  const [doing, setDoing] = useState(false);
  const handle = async (e) => {
    e.stopPropagation();
    if (doing) return;
    setDoing(true);
    try {
      await api.post(`${API}/zoho/tasks/${taskId}/complete`);
      onComplete(taskId);
    } catch { }
    finally { setDoing(false); }
  };
  return (
    <button
      type="button"
      onClick={handle}
      disabled={doing}
      className="flex items-center gap-1 text-[10px] font-medium text-green-700 hover:text-green-900 mt-0.5 disabled:opacity-50"
      data-testid={`task-complete-btn-${taskId}`}
    >
      {doing ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
      Done
    </button>
  );
};

const NOTIF_THEMES = {
  purple: { bg: 'bg-purple-50', activeBorder: 'border-purple-400', hoverBorder: 'hover:border-purple-300', text: 'text-purple-700', sub: 'text-purple-500', iconCls: 'bg-purple-600' },
  blue:   { bg: 'bg-blue-50',   activeBorder: 'border-blue-400',   hoverBorder: 'hover:border-blue-300',   text: 'text-blue-700',   sub: 'text-blue-500',   iconCls: 'bg-blue-600'   },
  orange: { bg: 'bg-orange-50', activeBorder: 'border-orange-400', hoverBorder: 'hover:border-orange-300', text: 'text-orange-700', sub: 'text-orange-500', iconCls: ''              },
  green:  { bg: 'bg-green-50',  activeBorder: 'border-green-400',  hoverBorder: 'hover:border-green-300',  text: 'text-green-700',  sub: 'text-green-500',  iconCls: 'bg-green-600'  },
  indigo: { bg: 'bg-indigo-50', activeBorder: 'border-indigo-400', hoverBorder: 'hover:border-indigo-300', text: 'text-indigo-700', sub: 'text-indigo-500', iconCls: 'bg-indigo-600' },
};

const NotifCard = ({ show, icon: Icon, label, count, loading, active, quiet, theme = 'purple', badge, onClick }) => {
  if (!show) return null;
  const th = NOTIF_THEMES[theme] || NOTIF_THEMES.purple;
  const borderCls = quiet
    ? 'border-gray-200 hover:border-gray-300'
    : active
      ? th.activeBorder
      : `border-gray-200 ${th.hoverBorder}`;
  const iconStyle = theme === 'orange' && !quiet ? { backgroundColor: '#ee7203' } : undefined;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative w-full h-20 flex flex-col items-center justify-center rounded-xl border-2 transition-all hover:shadow-lg gap-0.5 ${quiet ? 'bg-white' : th.bg} ${borderCls} ${active ? 'shadow-md' : ''}`}
    >
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center ${quiet ? 'bg-gray-300' : th.iconCls}`}
        style={iconStyle}
      >
        <Icon className="w-3.5 h-3.5 text-white" />
      </div>
      <span className={`text-xl font-bold leading-none ${quiet ? 'text-gray-400' : th.text}`}>
        {loading ? '—' : count}
      </span>
      <span className={`text-[9px] font-semibold uppercase tracking-wider ${quiet ? 'text-gray-400' : th.sub}`}>
        {label}
      </span>
      {badge > 0 && (
        <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold shadow-sm">
          {badge}
        </span>
      )}
      {active && (
        <div className="absolute -bottom-[5px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 rotate-45 bg-white border-r-2 border-b-2 border-gray-200 z-10" />
      )}
    </button>
  );
};

const UnifiedNotificationBar = ({
  defaultFeed,
  showCalls, showPi, showTasks, showStock, showOrders,
  calls, piCounts, tasks, stockSummary, quotesCount, quotesBreakdown,
  callsLoading, piLoading, tasksLoading, stockLoading,
  callsRefreshing, manualRefreshCalls,
  onCallClick, activeCall, onCloseCall, onCallCompleted, onCallDeleted,
  onTaskComplete, onTaskSaved,
  notifSettings,
  darkGlassEnabled,
}) => {
  const [activeFeed, setActiveFeed] = useState(defaultFeed);
  const [collapsed, setCollapsed]   = useState(true);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [showOverdueTasksModal, setShowOverdueTasksModal] = useState(false);
  const [showCompletedTasksModal, setShowCompletedTasksModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedTaskIsCompleted, setSelectedTaskIsCompleted] = useState(false);
  const [completedTodayTasks, setCompletedTodayTasks] = useState([]);
  const [showOverdueCallsModal, setShowOverdueCallsModal] = useState(false);
  const [showCompletedCallsModal, setShowCompletedCallsModal] = useState(false);
  const [completedCallsToday, setCompletedCallsToday] = useState([]);
  const [customerModalSearch, setCustomerModalSearch] = useState(null);
  const scrollRef = useRef(null);

  const [calls8x8Made, setCalls8x8Made] = useState([]);
  const [calls8x8Logged, setCalls8x8Logged] = useState([]);
  const [show8x8Modal, setShow8x8Modal] = useState(false);
  const fetch8x8 = useCallback(() => {
    api.get(`${API}/8x8/active-calls`)
      .then(r => { setCalls8x8Made(r.data?.calls_made || []); setCalls8x8Logged(r.data?.calls_logged || []); })
      .catch(() => {});
  }, []);
  // `calls_made` from the backend is EVERY still-pending call regardless of date (so
  // the expanded modal can still show/action older ones) — but the small card's badge
  // number is meant to read "today's calls to log", not "all-time backlog", so it's
  // filtered down to today here.
  const isSameLocalDay = (iso, ref) => {
    if (!iso) return false;
    const d = new Date(iso);
    return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth() && d.getDate() === ref.getDate();
  };
  const calls8x8MadeToday = useMemo(() => {
    const today = new Date();
    return calls8x8Made.filter((c) => isSameLocalDay(c.created_at, today));
  }, [calls8x8Made]);
  useEffect(() => {
    fetch8x8();
    const id = setInterval(fetch8x8, 20000);
    return () => clearInterval(id);
  }, [fetch8x8]);

  // Instant push the moment the 8x8 poller stages a new call for this user —
  // the 20s interval above is just a safety net, not the primary mechanism.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    let es; let retryTimer;
    const connect = () => {
      es = new EventSource(`${API}/calls/sse?token=${encodeURIComponent(token)}`);
      es.addEventListener('8x8_calls_changed', fetch8x8);
      es.onerror = () => { es.close(); retryTimer = setTimeout(connect, 5000); };
    };
    connect();
    return () => { es?.close(); clearTimeout(retryTimer); };
  }, [fetch8x8]);

  useEffect(() => {
    if (!showTasks) return;
    api.get(`${API}/zoho/my-completed-tasks-today`)
      .then(r => {
        const backendTasks = r.data?.tasks || [];
        if (backendTasks.length > 0) {
          setCompletedTodayTasks(prev => {
            const existingIds = new Set(prev.map(t => t.id));
            const newTasks = backendTasks.filter(t => !existingIds.has(t.id));
            return newTasks.length > 0 ? [...prev, ...newTasks] : prev;
          });
        }
      })
      .catch(() => {});
  }, [showTasks]); // eslint-disable-line
  const scrollContent = (dir) => scrollRef.current?.scrollBy({ left: dir * 260, behavior: 'smooth' });

  const overdueCount     = calls.filter(c => c.is_overdue).length;
  const overdueCalls     = calls.filter(c => c.is_overdue);
  const todayCallsOnly   = calls.filter(c => !c.is_overdue);
  const overdueTasks     = tasks.filter(t => t.is_overdue);
  const todayTasks       = tasks.filter(t => t.is_today && !t.is_overdue);
  const taskOverdueCount = overdueTasks.length;
  const taskTodayCount   = todayTasks.length;

  // Scheduled Calls "Completed" summary — calls marked Completed in CRM today that
  // have had notes added (mirrors completedTodayTasks above). Refetches on the same
  // CALLS_CHANGED_EVENT the rest of the calls feed already listens to.
  useEffect(() => {
    if (!showCalls) return;
    const fetchCompletedCalls = () => {
      api.get(`${API}/zoho/my-completed-calls-today`)
        .then(r => setCompletedCallsToday(r.data?.calls || []))
        .catch(() => {});
    };
    fetchCompletedCalls();
    window.addEventListener(CALLS_CHANGED_EVENT, fetchCompletedCalls);
    return () => window.removeEventListener(CALLS_CHANGED_EVENT, fetchCompletedCalls);
  }, [showCalls]);

  const handleInternalTaskComplete = (taskId) => {
    const allTasks = [...overdueTasks, ...todayTasks];
    const completed = allTasks.find(t => t.id === taskId);
    if (completed) {
      setCompletedTodayTasks(prev => [...prev.filter(t => t.id !== taskId), completed]);
    }
    onTaskComplete(taskId);
  };

  const handleReactivated = (taskId) => {
    setCompletedTodayTasks(prev => prev.filter(t => t.id !== taskId));
  };

  const barFeeds = [
    showCalls  && 'calls',
    showPi     && 'pi',
    showTasks  && 'tasks',
    showStock  && 'stock',
  ].filter(Boolean);

  useEffect(() => {
    if (!barFeeds.includes(activeFeed) && barFeeds.length > 0) {
      setActiveFeed(barFeeds[0]);
    }
  }, [showCalls, showPi, showTasks, showStock]); // eslint-disable-line

  const callsHeadline = (() => {
    const today = calls.length - overdueCount;
    if (overdueCount > 0 && today > 0) return `${overdueCount} overdue · ${today} today`;
    if (overdueCount > 0) return `${overdueCount} overdue scheduled call${overdueCount > 1 ? 's' : ''}`;
    return `${today} scheduled call${today !== 1 ? 's' : ''} today`;
  })();

  const officeCheckCount = piCounts.filter(c => c.action_type === 'Office Check').length;
  const adjustmentCount  = piCounts.filter(c => c.action_type === 'Adjustment').length;
  const readyCount       = piCounts.length - officeCheckCount - adjustmentCount;
  const piHeadlineParts  = [];
  if (officeCheckCount > 0) piHeadlineParts.push(`${officeCheckCount} office check`);
  if (adjustmentCount  > 0) piHeadlineParts.push(`${adjustmentCount} adjustment${adjustmentCount > 1 ? 's' : ''}`);
  if (readyCount       > 0) piHeadlineParts.push(`${readyCount} ready to count`);
  const piHeadline = piHeadlineParts.join(' · ');

  const tasksHeadline = taskTodayCount > 0
    ? `${taskTodayCount} task${taskTodayCount !== 1 ? 's' : ''} due today`
    : 'No tasks today';

  const isCalls = activeFeed === 'calls' && showCalls;
  const isPi    = activeFeed === 'pi'    && showPi;
  const isTasks = activeFeed === 'tasks' && showTasks;
  const isStock = activeFeed === 'stock' && showStock;

  const wrapperClass = (() => {
    if (isCalls) return `rounded-b-2xl border backdrop-blur-[2px] py-2 ${overdueCount > 0 ? 'border-red-200/80 bg-red-50/70' : 'border-purple-200/80 bg-purple-50/70'}`;
    if (isPi)    return 'rounded-b-2xl border border-orange-200/80 bg-orange-50/70 backdrop-blur-[2px] py-2';
    if (isTasks) return 'rounded-b-2xl border border-blue-200/80 bg-blue-50/70 backdrop-blur-[2px] py-2';
    if (isStock) return 'rounded-b-2xl border border-green-200/80 bg-green-50/70 backdrop-blur-[2px] py-2';
    return 'rounded-b-2xl border border-gray-200/80 bg-gray-50/70 backdrop-blur-[2px] py-2';
  })();

  const arrowClass = (() => {
    if (isCalls) return overdueCount > 0 ? 'bg-red-50 hover:bg-red-100 border-red-200 text-red-700' : 'bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700';
    if (isPi)    return 'bg-orange-50 hover:bg-orange-100 border-orange-200 text-orange-700';
    if (isTasks) return 'bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700';
    if (isStock) return 'bg-green-50 hover:bg-green-100 border-green-200 text-green-700';
    return 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-700';
  })();

  const IconComp = isCalls ? Phone : isPi ? AlertCircle : isTasks ? CheckSquare : Package;

  const curIdx  = barFeeds.indexOf(activeFeed);
  const prevFeed = barFeeds[(curIdx - 1 + barFeeds.length) % barFeeds.length];
  const nextFeed = barFeeds[(curIdx + 1) % barFeeds.length];
  const canSwitch = barFeeds.length > 1;

  return (
    <>
      {collapsed ? (
        <CollapsedNotificationCards
          notifSettings={notifSettings}
          showCalls={showCalls}
          showPi={showPi}
          showTasks={showTasks}
          showStock={showStock}
          showOrders={showOrders}
          callsCount={calls.length}
          piCount={piCounts.length}
          tasksCount={tasks.length}
          taskTodayCount={taskTodayCount}
          stockTotal={stockSummary?.total_active || 0}
          stockUsers={stockSummary?.users || []}
          quotesCount={quotesCount}
          quotesBreakdown={quotesBreakdown}
          overdueCount={overdueCount}
          taskOverdueCount={taskOverdueCount}
          callsHeadline={callsHeadline}
          piHeadline={piHeadline}
          tasksHeadline={tasksHeadline}
          callsLoading={callsLoading}
          piLoading={piLoading}
          tasksLoading={tasksLoading}
          stockLoading={stockLoading}
          calls8x8MadeCount={calls8x8MadeToday.length}
          calls8x8LoggedCount={calls8x8Logged.length}
          onExpandCalls={() => { setActiveFeed('calls'); setCollapsed(false); }}
          onExpandPi={() => { setActiveFeed('pi'); setCollapsed(false); }}
          onExpandTasks={() => { setActiveFeed('tasks'); setCollapsed(false); }}
          onExpandStock={() => { setActiveFeed('stock'); setCollapsed(false); }}
          onExpandOrders={() => setShowOrdersModal(true)}
          onExpand8x8={() => setShow8x8Modal(true)}
          onOpenOverdueTasks={() => setShowOverdueTasksModal(true)}
          darkGlassEnabled={darkGlassEnabled}
        />
      ) : (
        <div className="mb-4 relative max-w-[1400px] mx-auto">
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="w-full flex items-center justify-center gap-1.5 py-1 text-xs text-gray-400 bg-white hover:bg-gray-50 rounded-t-2xl border border-b-0 border-gray-200 transition-colors"
            data-testid="notification-bar-close-strip"
          >
            <ChevronUp className="w-3 h-3" />
            <span>Collapse</span>
          </button>

          <div
            className={`${wrapperClass} flex items-center gap-2 px-3 min-h-[72px]`}
            data-testid={`${activeFeed}-notification-bar`}
          >
            <div className="shrink-0 flex items-center gap-1.5">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center shadow-sm ${
                isCalls ? (overdueCount > 0 ? 'bg-red-600' : 'bg-purple-600') :
                isPi    ? '' :
                isTasks ? 'bg-blue-600' :
                isStock ? 'bg-green-600' : 'bg-gray-500'
              }`} style={isPi ? { background: '#ee7203' } : undefined}>
                <IconComp className="w-3.5 h-3.5 text-white" />
              </div>
              {isCalls && (
                <button
                  type="button"
                  onClick={manualRefreshCalls}
                  title="Refresh calls"
                  className={`w-6 h-6 flex items-center justify-center rounded-full transition-colors ${arrowClass} border`}
                >
                  <RefreshCw className={`w-3 h-3 ${callsRefreshing ? 'animate-spin' : ''}`} />
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => scrollContent(-1)}
              className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-full border transition-colors ${arrowClass}`}
              title="Scroll left"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>

            <div
              ref={scrollRef}
              className="flex-1 overflow-x-auto min-w-0"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <div className="flex items-stretch gap-3 flex-nowrap py-1 px-1">
                {isCalls  && (
                  <>
                    {overdueCalls.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowOverdueCallsModal(true)}
                        className="shrink-0 flex flex-col items-center rounded-2xl bg-red-50 border-2 border-red-300 hover:border-red-500 hover:shadow-md px-4 pt-2 pb-3 min-w-[100px] transition-all"
                        data-testid="calls-overdue-summary-card"
                      >
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 mb-1">Overdue</span>
                        <span className="text-2xl font-bold text-red-700 leading-none">{overdueCalls.length}</span>
                        <span className="text-[10px] text-red-500 font-medium mt-0.5">View all</span>
                      </button>
                    )}
                    {completedCallsToday.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowCompletedCallsModal(true)}
                        className="shrink-0 flex flex-col items-center rounded-2xl bg-green-50 border-2 border-green-300 hover:border-green-500 hover:shadow-md px-4 pt-2 pb-3 min-w-[100px] transition-all"
                        data-testid="calls-completed-today-card"
                      >
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 mb-1">Completed</span>
                        <span className="text-2xl font-bold text-green-700 leading-none">{completedCallsToday.length}</span>
                        <span className="text-[10px] text-green-500 font-medium mt-0.5">Today</span>
                      </button>
                    )}
                    <CallPills calls={todayCallsOnly} callsRefreshing={callsRefreshing} onCallClick={onCallClick} />
                  </>
                )}
                {isPi     && <PICountPills piCounts={piCounts} />}
                {isTasks  && (
                  <>
                    {taskOverdueCount > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowOverdueTasksModal(true)}
                        className="shrink-0 flex flex-col items-center rounded-2xl bg-red-50 border-2 border-red-300 hover:border-red-500 hover:shadow-md px-4 pt-2 pb-3 min-w-[100px] transition-all"
                        data-testid="tasks-overdue-summary-card"
                      >
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 mb-1">Overdue</span>
                        <span className="text-2xl font-bold text-red-700 leading-none">{taskOverdueCount}</span>
                        <span className="text-[10px] text-red-500 font-medium mt-0.5">View all</span>
                      </button>
                    )}
                    {completedTodayTasks.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowCompletedTasksModal(true)}
                        className="shrink-0 flex flex-col items-center rounded-2xl bg-green-50 border-2 border-green-300 hover:border-green-500 hover:shadow-md px-4 pt-2 pb-3 min-w-[100px] transition-all"
                        data-testid="tasks-completed-today-card"
                      >
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 mb-1">Completed</span>
                        <span className="text-2xl font-bold text-green-700 leading-none">{completedTodayTasks.length}</span>
                        <span className="text-[10px] text-green-500 font-medium mt-0.5">Today</span>
                      </button>
                    )}
                    <TaskPills
                      tasks={todayTasks}
                      onComplete={handleInternalTaskComplete}
                      onTaskCardClick={setSelectedTask}
                      onCustomerClick={setCustomerModalSearch}
                    />
                  </>
                )}
                {isStock  && <StockRequestsPills stockSummary={stockSummary} />}
              </div>
            </div>

            <button
              type="button"
              onClick={() => scrollContent(1)}
              className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-full border transition-colors ${arrowClass}`}
              title="Scroll right"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>

            {canSwitch && (
              <div className="shrink-0 flex items-center gap-0.5 border-l pl-2 ml-1">
                <button
                  type="button"
                  onClick={() => setActiveFeed(prevFeed)}
                  className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-200/60 transition-colors"
                  title="Previous feed"
                >
                  <ChevronLeft className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  onClick={() => setActiveFeed(nextFeed)}
                  className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-200/60 transition-colors"
                  title="Next feed"
                >
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showOrdersModal && (
        <OrdersQuotesModal
          onClose={() => setShowOrdersModal(false)}
          quotesBreakdown={quotesBreakdown}
        />
      )}

      <EightXEightCallsModal open={show8x8Modal} onClose={() => setShow8x8Modal(false)} />

      {showOverdueTasksModal && (
        <OverdueTasksModal
          tasks={overdueTasks}
          onClose={() => setShowOverdueTasksModal(false)}
          onTaskClick={setSelectedTask}
        />
      )}

      {showCompletedTasksModal && (
        <CompletedTasksModal
          tasks={completedTodayTasks}
          onClose={() => setShowCompletedTasksModal(false)}
          onTaskClick={(t) => {
            setSelectedTask(t);
            setSelectedTaskIsCompleted(true);
            setShowCompletedTasksModal(false);
          }}
        />
      )}

      {showOverdueCallsModal && (
        <OverdueCallsModal
          calls={overdueCalls}
          onClose={() => setShowOverdueCallsModal(false)}
          onCallClick={(c) => { onCallClick(c); setShowOverdueCallsModal(false); }}
        />
      )}

      {showCompletedCallsModal && (
        <CompletedCallsModal
          calls={completedCallsToday}
          onClose={() => setShowCompletedCallsModal(false)}
        />
      )}

      {selectedTask && (
        <TaskDetailsModal
          task={selectedTask}
          isCompleted={selectedTaskIsCompleted}
          onClose={() => { setSelectedTask(null); setSelectedTaskIsCompleted(false); }}
          onComplete={(id) => { handleInternalTaskComplete(id); setSelectedTask(null); setSelectedTaskIsCompleted(false); }}
          onSaved={(id) => { onTaskSaved(id); setSelectedTask(null); setSelectedTaskIsCompleted(false); }}
          onReactivated={(id) => { handleReactivated(id); setSelectedTask(null); setSelectedTaskIsCompleted(false); }}
        />
      )}

      {customerModalSearch !== null && (
        <CustomerManagerModal
          searchName={customerModalSearch}
          onClose={() => setCustomerModalSearch(null)}
        />
      )}
    </>
  );
};

const GLASS_OUTER_STYLE = {
  background: 'linear-gradient(160deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 50%, rgba(255,255,255,0.07) 100%)',
  backdropFilter: 'blur(20px) saturate(1.5)',
  WebkitBackdropFilter: 'blur(20px) saturate(1.5)',
  border: '1px solid rgba(255,255,255,0.18)',
  boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.22), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.2)',
};
const GLASS_TEXT_MUTED = { color: 'rgba(255,255,255,0.55)' };
const GLASS_TEXT_LIGHT = { color: 'rgba(255,255,255,0.92)' };

const CollapsedNotificationCards = ({
  notifSettings,
  showCalls, showPi, showTasks, showStock, showOrders,
  callsCount, piCount, tasksCount, taskTodayCount,
  stockTotal, stockUsers,
  quotesCount, quotesBreakdown,
  overdueCount, taskOverdueCount,
  callsHeadline, piHeadline, tasksHeadline,
  callsLoading, piLoading, tasksLoading, stockLoading,
  calls8x8MadeCount, calls8x8LoggedCount,
  onExpandCalls, onExpandPi, onExpandTasks, onExpandStock, onExpandOrders, onExpand8x8,
  onOpenOverdueTasks,
  darkGlassEnabled,
}) => {
  const [stockHover, setStockHover] = useState(false);

  const tile = darkGlassEnabled
    ? 'relative w-full h-20 flex flex-col items-center justify-center rounded-xl transition-all hover:shadow-lg hover:scale-[1.02] gap-0.5 cursor-pointer'
    : 'relative w-full h-20 flex flex-col items-center justify-center rounded-xl border-2 transition-all hover:shadow-lg hover:scale-[1.02] gap-0.5 cursor-pointer';

  const ALL_TYPES = ['calls', 'tasks', 'pi_counts', 'stock', 'orders_quotes'];
  const rawNotifs = notifSettings?.notifications || [];
  const mergedNotifs = [...rawNotifs];
  ALL_TYPES.forEach((type, idx) => {
    if (!mergedNotifs.find(n => n.type === type)) {
      mergedNotifs.push({ type, enabled: true, order: 100 + idx });
    }
  });
  const sortedTypes = [...mergedNotifs].sort(
    (a, b) => {
      const posA = typeof a.position === 'number' ? a.position : (typeof a.order === 'number' ? a.order : 999);
      const posB = typeof b.position === 'number' ? b.position : (typeof b.order === 'number' ? b.order : 999);
      return posA - posB;
    }
  );

  const showMap = {
    calls: false, // retired from the top notification row — the 8x8 card (always shown) replaces it
    tasks: showTasks,
    pi_counts: showPi,
    stock: showStock,
    orders_quotes: showOrders,
    eight_x8: true,
  };

  const renderCard = (n) => {
    const { type } = n;
    const settingEnabled = n.enabled !== false;

    if (!settingEnabled) {
      // Hidden by an admin — disappear entirely (no empty placeholder box); the flex
      // container below re-centers whatever cards remain visible.
      return null;
    }

    if (!showMap[type]) return null;

    if (type === 'calls') {
      const empty = callsCount === 0;
      return (
        <button
          key="calls"
          type="button"
          onClick={onExpandCalls}
          data-testid="notification-card-calls"
          className={darkGlassEnabled ? tile : `${tile} ${
            empty
              ? 'bg-white border-gray-200 hover:border-gray-300'
              : overdueCount > 0
                ? 'bg-red-50 border-red-200 hover:border-red-400'
                : 'bg-purple-50 border-purple-200 hover:border-purple-400'
          }`}
          style={darkGlassEnabled ? GLASS_OUTER_STYLE : undefined}
          title={empty ? 'No calls scheduled today' : callsHeadline}
        >
          <div className={`w-7 h-7 rounded-full flex items-center justify-center shadow-sm ${
            empty ? 'bg-gray-300' : overdueCount > 0 ? 'bg-red-600' : 'bg-purple-600'
          }`}>
            <Phone className="w-3.5 h-3.5 text-white" />
          </div>
          <span
            className={darkGlassEnabled ? 'text-xl font-bold leading-none' : `text-xl font-bold leading-none ${
              empty ? 'text-gray-400' : overdueCount > 0 ? 'text-red-700' : 'text-purple-700'
            }`}
            style={darkGlassEnabled ? (empty ? GLASS_TEXT_MUTED : GLASS_TEXT_LIGHT) : undefined}
          >{callsLoading ? '—' : callsCount}</span>
          <span
            className={darkGlassEnabled ? 'text-[9px] font-semibold uppercase tracking-wider' : `text-[9px] font-semibold uppercase tracking-wider ${
              empty ? 'text-gray-400' : overdueCount > 0 ? 'text-red-500' : 'text-purple-500'
            }`}
            style={darkGlassEnabled ? GLASS_TEXT_MUTED : undefined}
          >Calls</span>
        </button>
      );
    }

    if (type === 'tasks') {
      const tasksEmpty = taskTodayCount === 0;
      const callsEmpty = callsCount === 0;
      return (
        <div
          key="tasks"
          data-testid="notification-card-tasks"
          className={darkGlassEnabled
            ? 'relative w-full h-20 flex rounded-xl transition-all hover:shadow-lg'
            : 'relative w-full h-20 flex rounded-xl border-2 border-gray-200 transition-all hover:shadow-lg'}
          style={darkGlassEnabled ? GLASS_OUTER_STYLE : undefined}
        >
          <button
            type="button"
            onClick={onExpandTasks}
            data-testid="notification-card-tasks-half"
            title={taskTodayCount === 0 ? 'No tasks due today' : tasksHeadline}
            className={`relative flex-1 h-full flex flex-col items-center justify-center gap-0.5 rounded-l-xl transition-colors ${
              darkGlassEnabled ? '' : (tasksEmpty ? 'bg-white hover:bg-gray-50' : 'bg-blue-50 hover:bg-blue-100')
            }`}
          >
            <div className={`w-7 h-7 rounded-full flex items-center justify-center shadow-sm ${taskTodayCount === 0 ? 'bg-gray-300' : 'bg-blue-600'}`}>
              <CheckSquare className="w-3.5 h-3.5 text-white" />
            </div>
            <span
              className={darkGlassEnabled ? 'text-xl font-bold leading-none' : `text-xl font-bold leading-none ${tasksEmpty ? 'text-gray-400' : 'text-blue-700'}`}
              style={darkGlassEnabled ? (tasksEmpty ? GLASS_TEXT_MUTED : GLASS_TEXT_LIGHT) : undefined}
            >
              {tasksLoading ? '—' : taskTodayCount}
            </span>
            <span
              className={darkGlassEnabled ? 'text-[9px] font-semibold uppercase tracking-wider' : `text-[9px] font-semibold uppercase tracking-wider ${tasksEmpty ? 'text-gray-400' : 'text-blue-500'}`}
              style={darkGlassEnabled ? GLASS_TEXT_MUTED : undefined}
            >
              Tasks
            </span>
          </button>

          {showCalls && (
            <>
              <div className={`w-px self-stretch my-3 ${darkGlassEnabled ? 'bg-white/15' : 'bg-gray-200'}`} />
              <button
                type="button"
                onClick={onExpandCalls}
                data-testid="notification-card-calls-half"
                title={callsEmpty ? 'No calls scheduled today' : callsHeadline}
                className={`relative flex-1 h-full flex flex-col items-center justify-center gap-0.5 rounded-r-xl transition-colors ${
                  darkGlassEnabled ? '' : (callsEmpty ? 'bg-white hover:bg-gray-50' : 'bg-purple-50 hover:bg-purple-100')
                }`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center shadow-sm ${callsEmpty ? 'bg-gray-300' : 'bg-purple-600'}`}>
                  <Phone className="w-3.5 h-3.5 text-white" />
                </div>
                <span
                  className={darkGlassEnabled ? 'text-xl font-bold leading-none' : `text-xl font-bold leading-none ${callsEmpty ? 'text-gray-400' : 'text-purple-700'}`}
                  style={darkGlassEnabled ? (callsEmpty ? GLASS_TEXT_MUTED : GLASS_TEXT_LIGHT) : undefined}
                >
                  {callsLoading ? '—' : callsCount}
                </span>
                <span
                  className={darkGlassEnabled ? 'text-[9px] font-semibold uppercase tracking-wider leading-tight text-center' : `text-[9px] font-semibold uppercase tracking-wider leading-tight text-center ${callsEmpty ? 'text-gray-400' : 'text-purple-500'}`}
                  style={darkGlassEnabled ? GLASS_TEXT_MUTED : undefined}
                >
                  Sched Calls
                </span>
              </button>
            </>
          )}

          {/* Overdue badges sit on the outer card corners (not clipped inside either
              half) so they're always fully visible on top of the card. */}
          {taskOverdueCount > 0 && (
            <span className="absolute -top-2 -left-1.5 z-10 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[9px] font-bold shadow-md ring-2 ring-white">
              {taskOverdueCount}
            </span>
          )}
          {showCalls && overdueCount > 0 && (
            <span className="absolute -top-2 -right-1.5 z-10 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[9px] font-bold shadow-md ring-2 ring-white">
              {overdueCount}
            </span>
          )}
        </div>
      );
    }

    if (type === 'pi_counts') {
      const empty = piCount === 0;
      return (
        <button
          key="pi_counts"
          type="button"
          onClick={onExpandPi}
          data-testid="notification-card-pi-counts"
          className={darkGlassEnabled ? tile : `${tile} ${empty ? 'bg-white border-gray-200 hover:border-gray-300' : 'bg-orange-50 border-orange-200 hover:border-orange-400'}`}
          style={darkGlassEnabled ? GLASS_OUTER_STYLE : undefined}
          title={empty ? 'No pending PI counts' : piHeadline}
        >
          <div className="w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
            style={{ backgroundColor: empty ? '#d1d5db' : '#ee7203' }}>
            <ClipboardList className="w-3.5 h-3.5 text-white" />
          </div>
          <span
            className={darkGlassEnabled ? 'text-xl font-bold leading-none' : `text-xl font-bold leading-none ${empty ? 'text-gray-400' : 'text-orange-700'}`}
            style={darkGlassEnabled ? (empty ? GLASS_TEXT_MUTED : GLASS_TEXT_LIGHT) : undefined}
          >
            {piLoading ? '—' : piCount}
          </span>
          <span
            className={darkGlassEnabled ? 'text-[9px] font-semibold uppercase tracking-wider' : `text-[9px] font-semibold uppercase tracking-wider ${empty ? 'text-gray-400' : 'text-orange-500'}`}
            style={darkGlassEnabled ? GLASS_TEXT_MUTED : undefined}
          >
            PI Counts
          </span>
        </button>
      );
    }

    if (type === 'stock') {
      const empty = stockTotal === 0;
      return (
        <div
          key="stock"
          className="relative"
          onMouseEnter={() => setStockHover(true)}
          onMouseLeave={() => setStockHover(false)}
        >
          <button
            type="button"
            onClick={onExpandStock}
            data-testid="notification-card-stock"
            className={darkGlassEnabled ? tile : `${tile} ${empty ? 'bg-white border-gray-200 hover:border-gray-300' : 'bg-green-50 border-green-200 hover:border-green-400'}`}
            style={darkGlassEnabled ? GLASS_OUTER_STYLE : undefined}
            title={empty ? 'No pending stock requests' : `${stockTotal} stock item${stockTotal !== 1 ? 's' : ''} pending`}
          >
            <div className={`w-7 h-7 rounded-full flex items-center justify-center shadow-sm ${empty ? 'bg-gray-300' : 'bg-green-600'}`}>
              <ShoppingCart className="w-3.5 h-3.5 text-white" />
            </div>
            <span
              className={darkGlassEnabled ? 'text-xl font-bold leading-none' : `text-xl font-bold leading-none ${empty ? 'text-gray-400' : 'text-green-700'}`}
              style={darkGlassEnabled ? (empty ? GLASS_TEXT_MUTED : GLASS_TEXT_LIGHT) : undefined}
            >
              {stockLoading ? '—' : stockTotal}
            </span>
            <span
              className={darkGlassEnabled ? 'text-[9px] font-semibold uppercase tracking-wider' : `text-[9px] font-semibold uppercase tracking-wider ${empty ? 'text-gray-400' : 'text-green-500'}`}
              style={darkGlassEnabled ? GLASS_TEXT_MUTED : undefined}
            >
              Stock
            </span>
          </button>
          {stockHover && stockUsers.length > 0 && (
            <div
              className="absolute left-0 top-full mt-1.5 z-30 bg-white border border-green-200 rounded-xl shadow-xl min-w-[240px] py-2"
              data-testid="stock-hover-tooltip"
            >
              {stockUsers.map((u, i) => (
                <div key={i} className="flex items-start gap-2 px-3 py-1.5 hover:bg-green-50">
                  <Users2 className="w-3.5 h-3.5 text-green-600 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <span className="font-semibold text-xs text-gray-800">{u.user_name}</span>
                    <span className="text-xs text-gray-500 mx-1">·</span>
                    <span className="text-xs text-green-700 font-medium">{u.count} item{u.count !== 1 ? 's' : ''}</span>
                    {u.suppliers && u.suppliers.length > 0 && (
                      <div className="text-[10px] text-gray-400 truncate max-w-[180px]">{u.suppliers.join(', ')}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (type === 'orders_quotes') {
      const qVal = quotesBreakdown?.quotes_value || 0;
      const oVal = quotesBreakdown?.orders_value || 0;
      const fmtVal = (v) => {
        if (!v) return '—';
        return v >= 1000
          ? `£${(v / 1000).toFixed(1)}k`
          : `£${v.toFixed(0)}`;
      };
      const accent = darkGlassEnabled ? '#ff9838' : '#ee7203';

      return (
        <button
          key="orders_quotes"
          type="button"
          onClick={onExpandOrders}
          data-testid="notification-card-orders"
          title="View your quotes & orders"
          className="relative w-full cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02]"
          style={darkGlassEnabled ? { ...GLASS_OUTER_STYLE, borderRadius: '12px', minHeight: '5.5rem' } : {
            background: 'white',
            border: '2px solid #ee7203',
            borderRadius: '12px',
            minHeight: '5.5rem',
          }}
        >
          <div className="flex w-full h-full items-stretch px-2 py-2 gap-1">
            <div className="flex-1 flex flex-col items-center justify-between gap-0.5">
              <span className="text-[11px] font-bold uppercase tracking-wider leading-none" style={{ color: accent }}>
                Quotes
              </span>
              <span className="text-2xl font-extrabold leading-none" style={{ color: accent }}>
                {quotesBreakdown?.quotes ?? 0}
              </span>
              <span className="text-[11px] font-semibold leading-none" style={darkGlassEnabled ? GLASS_TEXT_MUTED : { color: '#6b7280' }}>{fmtVal(qVal)}</span>
            </div>

            <div className="flex flex-col items-center justify-center gap-1 px-1">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center shadow-sm flex-shrink-0"
                style={{ backgroundColor: accent }}
              >
                <FileText className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="w-px flex-1" style={{ backgroundColor: darkGlassEnabled ? 'rgba(255,255,255,0.15)' : '#fed7aa' }} />
            </div>

            <div className="flex-1 flex flex-col items-center justify-between gap-0.5">
              <span className="text-[11px] font-bold uppercase tracking-wider leading-none" style={{ color: accent }}>
                Orders
              </span>
              <span className="text-2xl font-extrabold leading-none" style={{ color: accent }}>
                {quotesBreakdown?.orders ?? 0}
              </span>
              <span className="text-[11px] font-semibold leading-none" style={darkGlassEnabled ? GLASS_TEXT_MUTED : { color: '#6b7280' }}>{fmtVal(oVal)}</span>
            </div>
          </div>
        </button>
      );
    }

    if (type === 'eight_x8') {
      const total = calls8x8MadeCount + calls8x8LoggedCount;
      const accentText = darkGlassEnabled ? 'rgba(255,255,255,0.92)' : EIGHT_X_EIGHT_DARK;
      return (
        <button
          key="eight_x8"
          type="button"
          onClick={onExpand8x8}
          data-testid="notification-card-8x8"
          title="8x8 calls made vs. logged in CRM"
          className="relative w-full cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02]"
          style={darkGlassEnabled ? { ...GLASS_OUTER_STYLE, borderRadius: '12px', minHeight: '5.5rem' } : {
            background: 'white',
            border: `2px solid ${EIGHT_X_EIGHT_LIME}`,
            borderRadius: '12px',
            minHeight: '5.5rem',
          }}
        >
          <div className="flex w-full h-full items-stretch px-2 py-2 gap-1">
            <div className="flex-1 flex flex-col items-center justify-between gap-0.5">
              <span className="text-[11px] font-bold uppercase tracking-wider leading-none" style={{ color: accentText }}>
                8x8 Calls
              </span>
              <span className="text-2xl font-extrabold leading-none" style={{ color: accentText }}>
                {calls8x8MadeCount}
              </span>
              <span className="text-[11px] font-semibold leading-none" style={darkGlassEnabled ? GLASS_TEXT_MUTED : { color: '#6b7280' }}>to log</span>
            </div>

            <div className="flex flex-col items-center justify-center gap-1 px-1">
              <div className="w-7 h-7 rounded-full flex items-center justify-center shadow-sm flex-shrink-0" style={{ backgroundColor: EIGHT_X_EIGHT_LIME }}>
                <PhoneCall className="w-3.5 h-3.5" style={{ color: EIGHT_X_EIGHT_DARK }} />
              </div>
              <div className="w-px flex-1" style={{ backgroundColor: darkGlassEnabled ? 'rgba(255,255,255,0.15)' : EIGHT_X_EIGHT_LIME }} />
            </div>

            <div className="flex-1 flex flex-col items-center justify-between gap-0.5">
              <span className="text-[11px] font-bold uppercase tracking-wider leading-none" style={{ color: accentText }}>
                Logged
              </span>
              <span className="text-2xl font-extrabold leading-none" style={{ color: accentText }}>
                {calls8x8LoggedCount}
              </span>
              <span className="text-[11px] font-semibold leading-none" style={darkGlassEnabled ? GLASS_TEXT_MUTED : { color: '#6b7280' }}>today</span>
            </div>
          </div>
        </button>
      );
    }

    return null;
  };

  return (
    <div
      className="w-full max-w-[1400px] mx-auto flex flex-wrap justify-center gap-2 md:gap-6 mb-4"
      data-testid="notification-cards-row"
    >
      {sortedTypes.map(n => {
        const card = renderCard(n);
        if (!card) return null;
        return (
          <div key={n.type} className="basis-[calc(50%-4px)] md:basis-[calc(33.333%-16px)] lg:basis-[calc(20%-19.2px)] grow-0 shrink-0">
            {card}
          </div>
        );
      })}
      {(() => {
        const card = renderCard({ type: 'eight_x8', enabled: true });
        if (!card) return null;
        return (
          <div className="basis-[calc(50%-4px)] md:basis-[calc(33.333%-16px)] lg:basis-[calc(20%-19.2px)] grow-0 shrink-0">
            {card}
          </div>
        );
      })()}
    </div>
  );
};

const CallPills = ({ calls, callsRefreshing, onCallClick }) => (
  <div className="flex gap-3 flex-nowrap">
    {calls.length === 0 && !callsRefreshing && (
      <div className="inline-flex items-center gap-1.5 text-sm text-purple-500 px-4">No calls scheduled today</div>
    )}
    {calls.map(c => (
      <button
        key={c.id}
        type="button"
        onClick={() => onCallClick(c)}
        className={`shrink-0 flex flex-col items-center rounded-2xl bg-white border px-2 pt-2 pb-3 transition-all w-[130px] overflow-hidden hover:shadow-md ${
          c.is_overdue ? 'border-red-300 hover:border-red-500' : 'border-purple-200 hover:border-purple-400'
        }`}
        data-testid={`call-notification-item-${c.id}`}
      >
        <div className="flex flex-col items-center text-center min-w-0 w-full">
          {c.is_overdue && (
            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 mb-1">Overdue</span>
          )}
          <span className="font-semibold text-sm text-gray-900 truncate max-w-full">{c.account?.name || 'Unknown account'}</span>
          <span className="text-[11px] text-gray-600 flex items-center gap-1.5">
            <Phone className="w-2.5 h-2.5" />
            <span className="truncate">{c.account?.phone || c.phone || 'No number on file'}</span>
          </span>
          {c.description && !c.tag && (
            <span className="text-[11px] text-gray-500 italic mt-1 line-clamp-2 max-w-full">{c.description}</span>
          )}
          {c.tag && (
            <span
              className="mt-1.5 text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200 max-w-full truncate"
              data-testid={`call-tag-pill-${c.id}`}
            >
              {c.tag}
            </span>
          )}
        </div>
      </button>
    ))}
    {callsRefreshing && (
      <div className="inline-flex items-center gap-1.5 text-[11px] text-purple-700 shrink-0">
        <Loader2 className="w-3 h-3 animate-spin" /> Refreshing…
      </div>
    )}
  </div>
);

const PICountPills = ({ piCounts }) => {
  const navigate = useNavigate();
  const [activeMenuId, setActiveMenuId] = useState(null);

  useEffect(() => {
    if (!activeMenuId) return;
    const onDoc = () => setActiveMenuId(null);
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [activeMenuId]);

  const fmt = (d) => {
    if (!d) return '';
    try { const dt = new Date(d); return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getFullYear()).slice(-2)}`; }
    catch { return ''; }
  };

  return (
    <div className="flex gap-3 flex-nowrap">
      {piCounts.map(c => {
        const isMenuOpen = activeMenuId === c.id;
        const total = c.total || 0;
        const counted = c.counted || 0;
        const progress = total > 0 ? Math.round((counted / total) * 100) : 0;
        const isAdjustment = c.action_type === 'Adjustment';
        const isOfficeCheck = c.action_type === 'Office Check';
        const badgeClass = isAdjustment ? 'bg-teal-100 text-teal-700' : 'bg-orange-100 text-orange-700';
        const badgeLabel = isAdjustment ? 'Adjustment' : isOfficeCheck ? 'Office Check' : 'Ready to Count';
        return (
          <div key={c.id} className="relative">
            <button
              type="button"
              onClick={e => { e.stopPropagation(); isAdjustment ? navigate(`/pi-counts/adjustment/${c.id}`) : setActiveMenuId(isMenuOpen ? null : c.id); }}
              className={`relative shrink-0 flex flex-col items-center rounded-2xl bg-white border px-2 pt-2 pb-2 transition-all w-[130px] overflow-hidden hover:shadow-md ${isAdjustment ? 'border-teal-200 hover:border-teal-400' : 'border-orange-200 hover:border-orange-400'}`}
              data-testid={`pi-count-notification-item-${c.id}`}
            >
              {c.is_recount && (
                <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[22px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-bold shadow-sm">
                  R{c.recount_number || 1}
                </span>
              )}
              <div className="flex flex-col items-center text-center min-w-0 w-full">
                <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full mb-0.5 ${badgeClass}`}>{badgeLabel}</span>
                <span className="font-semibold text-sm text-gray-900 truncate max-w-full leading-tight">{c.supplier_name}</span>
                <span className="text-[11px] text-gray-600 flex items-center gap-1.5 leading-tight">
                  <ClipboardList className="w-2.5 h-2.5" />
                  <span>{fmt(c.date)}{total > 0 ? ` · ${total} item${total === 1 ? '' : 's'}${!isAdjustment && counted ? ` · ${counted}/${total} (${progress}%)` : ''}` : ''}</span>
                </span>
              </div>
            </button>
            {isMenuOpen && !isAdjustment && (
              <div
                className="absolute z-30 right-0 mt-1 bg-white rounded-xl border-2 shadow-xl min-w-[170px]"
                style={{ borderColor: '#ee7203' }}
                onClick={e => e.stopPropagation()}
                data-testid={`pi-count-notification-menu-${c.id}`}
              >
                <button className="w-full px-4 py-3 text-left hover:bg-orange-50 flex items-center gap-3 text-sm font-medium rounded-t-xl" onClick={() => { setActiveMenuId(null); navigate(`/pi-counts/${c.id}`); }}>
                  <FileCheck className="w-5 h-5" style={{ color: '#ee7203' }} />
                  Open Count
                </button>
                <button className="w-full px-4 py-3 text-left hover:bg-orange-50 flex items-center gap-3 text-sm font-medium border-t" onClick={() => { setActiveMenuId(null); navigate(`/pi-counts?action=send-to-wh&countId=${encodeURIComponent(c.id)}`); }}>
                  <Send className="w-5 h-5 text-indigo-500" />
                  Send to W/H
                </button>
                <button className="w-full px-4 py-3 text-left hover:bg-orange-50 flex items-center gap-3 text-sm font-medium border-t rounded-b-xl" onClick={() => { setActiveMenuId(null); navigate(`/pi-counts/${c.id}?action=print`); }}>
                  <Printer className="w-5 h-5 text-gray-600" />
                  Print
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const TaskPills = ({ tasks, onComplete, onTaskCardClick, onCustomerClick }) => {
  const [completing, setCompleting] = useState(null);

  const fmtDate = (d) => {
    if (!d) return '';
    try {
      const dt = new Date(d);
      return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getFullYear()).slice(-2)}`;
    } catch { return d; }
  };

  const handleComplete = async (e, taskId) => {
    e.stopPropagation();
    if (completing) return;
    setCompleting(taskId);
    try {
      await api.post(`${API}/zoho/tasks/${taskId}/complete`);
      onComplete(taskId);
    } catch {
    } finally {
      setCompleting(null);
    }
  };

  return (
    <div className="flex gap-3 flex-nowrap">
      {tasks.length === 0 && (
        <div className="inline-flex items-center gap-1.5 text-sm text-blue-500 px-4">No tasks due today</div>
      )}
      {tasks.map(t => {
        const accountName = t.account_name || null;
        const relatedTo = t.account?.name || null;

        return (
        <div
          key={t.id}
          className="shrink-0 flex flex-col items-center rounded-xl bg-white border border-blue-200 hover:border-blue-400 hover:shadow-md px-2 pt-1.5 pb-2 w-[188px] overflow-hidden transition-all cursor-pointer"
          data-testid={`task-notification-item-${t.id}`}
          onClick={() => onTaskCardClick && onTaskCardClick(t)}
          title="Click to view task details"
        >
          <div className="flex flex-col items-center text-center min-w-0 w-full">
            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 mb-1">Due Today</span>
            {accountName && (
              <span className="font-semibold text-xs text-blue-700 truncate w-full leading-tight text-center" data-testid={`task-customer-name-${t.id}`}>
                {accountName}
              </span>
            )}
            <span className="text-xs text-gray-700 truncate w-full leading-tight mt-0.5">{t.subject}</span>
            {relatedTo && relatedTo !== accountName && (
              <span className="text-[10px] text-gray-400 truncate w-full leading-tight mt-0.5">{relatedTo}</span>
            )}
          </div>
        </div>
        );
      })}
    </div>
  );
};

const StockRequestsPills = ({ stockSummary }) => {
  const navigate = useNavigate();
  const users = stockSummary?.users || [];

  return (
    <div className="flex gap-3 flex-nowrap">
      {users.map((u, i) => (
        <button
          key={i}
          type="button"
          onClick={() => navigate(`/stock-management?requestUser=${encodeURIComponent(u.user_email)}`)}
          className="shrink-0 flex flex-col items-center rounded-2xl bg-white border border-green-200 hover:border-green-400 px-2 pt-2 pb-3 w-[130px] overflow-hidden transition-all hover:shadow-md"
          data-testid={`stock-request-user-${i}`}
          title={`View ${u.user_name}'s stock requests`}
        >
          <div className="flex flex-col items-center text-center min-w-0 w-full">
            <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center mb-1">
              <span className="text-xs font-bold text-green-700">{(u.user_name || '?').charAt(0).toUpperCase()}</span>
            </div>
            <span className="font-semibold text-sm text-gray-900 truncate max-w-full">{u.user_name}</span>
            <span className="text-xs text-green-700 font-medium">{u.count} item{u.count !== 1 ? 's' : ''}</span>
            {u.suppliers && u.suppliers.length > 0 && (
              <span className="text-[10px] text-gray-500 truncate max-w-full mt-0.5">{u.suppliers.join(', ')}</span>
            )}
          </div>
        </button>
      ))}
      {users.length === 0 && (
        <div className="inline-flex items-center text-sm text-gray-500 px-4 py-2">No pending requests</div>
      )}
    </div>
  );
};

export const OrdersQuotesModal = ({ onClose, quotesBreakdown }) => {
  const [activeTab, setActiveTab] = useState('quotes');
  const [quotes, setQuotes]       = useState([]);
  const [loading, setLoading]     = useState(true);

  const todayStr = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    api.get(`${API}/quotes/recent`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setQuotes(r.data?.quotes || []))
      .catch(() => setQuotes([]))
      .finally(() => setLoading(false));
  }, []);

  const isToday = (dateStr) => !!dateStr && String(dateStr).startsWith(todayStr);

  const todayQuotes = quotes.filter(q =>
    isToday(q.last_saved) &&
    (q.source !== 'automation_email' || q.quote_data?.email_type === 'quote')
  );
  const todayOrders = quotes.filter(q => q.source === 'automation_email' && q.quote_data?.email_type === 'order' && isToday(q.last_saved));

  const fmtDate = (d) => {
    if (!d) return '';
    try { const dt = new Date(d); return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getFullYear()).slice(-2)}`; }
    catch { return ''; }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl border border-indigo-200 w-full max-w-lg max-h-[70vh] flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="orders-quotes-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-indigo-600 flex items-center justify-center">
              <FileText className="w-3.5 h-3.5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800 leading-tight">Your Quotes & Orders</h3>
              <p className="text-[10px] text-gray-400 leading-none">Today only · yours only</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="orders-modal-close"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b px-5 pt-2">
          {[
            { key: 'quotes', label: `Quotes${quotesBreakdown?.quotes > 0 ? ` (${quotesBreakdown.quotes})` : ''}` },
            { key: 'orders', label: `Orders${quotesBreakdown?.orders > 0 ? ` (${quotesBreakdown.orders})` : ''}` },
          ].map(tab => (            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 pb-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-indigo-500 text-indigo-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`orders-modal-tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
            </div>
          ) : activeTab === 'quotes' ? (
            todayQuotes.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">No quotes raised today</p>
            ) : todayQuotes.map(q => {
              const qd = q.quote_data || {};
              const customer = qd.customer_name || qd.account_name || '—';
              const ref = qd.quote_number || qd.quote_ref || '';
              const val = qd.total_price || qd.grand_total || '';
              return (
                <div key={q.id} className={`flex items-center justify-between px-3 py-2 rounded-xl border text-sm ${q.zoho_quote_url ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`} data-testid={`quote-modal-item-${q.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-gray-800 truncate">{customer}</div>
                    <div className="text-xs text-gray-500">{ref && `${ref} · `}{fmtDate(q.last_saved)}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    {val && <span className="text-xs font-bold text-gray-700">£{Number(val).toLocaleString('en-GB', { minimumFractionDigits: 0 })}</span>}
                    {q.zoho_quote_url && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-600 text-white">In CRM</span>}
                  </div>
                </div>
              );
            })
          ) : (
            todayOrders.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">No orders received via automation today</p>
            ) : todayOrders.map(q => {
              const qd = q.quote_data || {};
              const label = qd.customer_name || '—';
              const ref = qd.quote_ref || qd.order_number || '';
              return (
                <div key={q.id} className="flex items-center justify-between px-3 py-2 rounded-xl border bg-gray-50 border-gray-200 text-sm" data-testid={`order-modal-item-${q.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-gray-800 truncate">{label}</div>
                    <div className="text-xs text-gray-500">{ref && `${ref} · `}{fmtDate(q.last_saved)}</div>
                  </div>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 shrink-0 ml-2">Order</span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

const CompletedTasksModal = ({ tasks, onClose, onTaskClick }) => {
  const fmtDate = (d) => {
    if (!d) return '';
    try {
      const dt = new Date(d);
      return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getFullYear()).slice(-2)}`;
    } catch { return ''; }
  };

  return (
    <div className="fixed inset-0 z-[10002] flex items-start justify-center pt-16 px-4" onClick={onClose} data-testid="completed-tasks-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl border border-green-200 w-full max-w-md max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="completed-tasks-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-green-100">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-green-600 flex items-center justify-center">
              <CheckCircle className="w-3.5 h-3.5 text-white" />
            </div>
            <h3 className="font-bold text-gray-800">Today's Completed</h3>
            <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full bg-green-100 text-green-700 text-xs font-bold">{tasks.length}</span>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" data-testid="completed-tasks-modal-close">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {tasks.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No tasks completed today</p>
          ) : tasks.map(t => {
            const rawName = t.account_name
              || (t.account?.module?.api_name === 'Accounts' ? t.account?.name : null)
              || '';
            const custName = rawName.replace(/\s*-\s*\d{4,}$/, '').trim();
            return (
              <div
                key={t.id}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-green-100 bg-green-50 cursor-pointer hover:border-green-300 hover:bg-green-100 transition-all"
                onClick={() => onTaskClick(t)}
                data-testid={`completed-task-item-${t.id}`}
              >
                <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm text-gray-900 truncate">{custName || '—'}</p>
                  <p className="text-xs text-gray-600 truncate mt-0.5">{t.subject}</p>
                  {t.due_date && (
                    <p className="text-[11px] text-green-600 flex items-center gap-1 mt-0.5">
                      <CalendarDays className="w-3 h-3" /> Due {fmtDate(t.due_date)}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CollapsedNotificationStrip = () => null;

const OverdueCallsModal = ({ calls, onClose, onCallClick }) => {
  const fmtDate = (d) => {
    if (!d) return '';
    try {
      const dt = new Date(d);
      return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getFullYear()).slice(-2)}`;
    } catch { return ''; }
  };

  return (
    <div className="fixed inset-0 z-[10002] flex items-start justify-center pt-16 px-4" onClick={onClose} data-testid="overdue-calls-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl border border-red-200 w-full max-w-md max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="overdue-calls-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-red-100">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-red-600 flex items-center justify-center">
              <Phone className="w-3.5 h-3.5 text-white" />
            </div>
            <h3 className="font-bold text-gray-800">Overdue Calls</h3>
            <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full bg-red-100 text-red-700 text-xs font-bold">{calls.length}</span>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" data-testid="overdue-calls-modal-close">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {calls.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No overdue calls</p>
          ) : calls.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => onCallClick(c)}
              className="w-full flex items-start justify-between gap-3 px-3 py-2.5 rounded-xl border border-red-100 bg-red-50 hover:bg-red-100 hover:border-red-200 transition-colors text-left"
              data-testid={`overdue-call-item-${c.id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm text-gray-900 truncate">{c.account?.name || c.contact?.name || '—'}</p>
                <p className="text-xs text-gray-600 truncate mt-0.5">{c.subject}</p>
                <p className="text-[11px] text-red-600 flex items-center gap-1 mt-0.5">
                  <CalendarDays className="w-3 h-3" /> Was due {fmtDate(c.call_start_time)}
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 mt-1" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const CompletedCallsModal = ({ calls, onClose }) => {
  const fmtDate = (d) => {
    if (!d) return '';
    try {
      const dt = new Date(d);
      return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getFullYear()).slice(-2)}`;
    } catch { return ''; }
  };

  return (
    <div className="fixed inset-0 z-[10002] flex items-start justify-center pt-16 px-4" onClick={onClose} data-testid="completed-calls-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl border border-green-200 w-full max-w-md max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="completed-calls-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-green-100">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-green-600 flex items-center justify-center">
              <CheckCircle className="w-3.5 h-3.5 text-white" />
            </div>
            <h3 className="font-bold text-gray-800">Calls Completed Today</h3>
            <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full bg-green-100 text-green-700 text-xs font-bold">{calls.length}</span>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" data-testid="completed-calls-modal-close">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {calls.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No calls completed today yet</p>
          ) : calls.map(c => (
            <div
              key={c.id}
              className="px-3 py-2.5 rounded-xl border border-green-100 bg-green-50"
              data-testid={`completed-call-item-${c.id}`}
            >
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm text-gray-900 truncate">{c.account?.name || c.contact?.name || '—'}</p>
                  <p className="text-xs text-gray-600 truncate mt-0.5">{c.subject}</p>
                  {c.notes && <p className="text-[11px] text-gray-500 mt-1 line-clamp-2">{c.notes}</p>}
                  <p className="text-[11px] text-green-600 flex items-center gap-1 mt-1">
                    <CalendarDays className="w-3 h-3" /> Completed {fmtDate(c.completed_time)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const QUOTE_STAGE_CONFIGS = [
  { value: 'Negotiation Tender',               bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
  { value: 'Negotiation Order to Place',       bg: '#fef3c7', text: '#b45309', border: '#fcd34d' },
  { value: 'Awaiting Supplier Information',    bg: '#fef9c3', text: '#854d0e', border: '#fde047' },
  { value: 'On Hold',                          bg: '#f3f4f6', text: '#374151', border: '#d1d5db' },
  { value: 'Closed Won Order in Progress',     bg: '#dcfce7', text: '#15803d', border: '#86efac' },
  { value: 'Closed Won Order Completed',       bg: '#d1fae5', text: '#065f46', border: '#34d399' },
  { value: 'Closed Lost',                      bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' },
];
const getStageConfig = (stage) =>
  QUOTE_STAGE_CONFIGS.find(s => s.value === stage) || { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' };
const AUTO_CLOSE_STAGES = ['Closed Won Order Completed', 'Closed Lost'];

export const TaskDetailsModal = ({ task, onClose, onComplete, isCompleted = false, onReactivated }) => {
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();
  const todayStr = new Date().toISOString().slice(0, 10);
  const enconOrange = '#ee7203';

  const [dueDate, setDueDate]         = useState(task.due_date ? task.due_date.slice(0, 10) : '');
  const [newNote, setNewNote]         = useState('');
  const [quoteStage, setQuoteStage]   = useState('');
  const [selectedOwnerId, setSelectedOwnerId] = useState(task.owner?.id || '');
  const [crmUsers, setCrmUsers]       = useState([]);
  const [crmUsersLoading, setCrmUsersLoading] = useState(false);
  const [quote, setQuote]             = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [taskNotes, setTaskNotes]     = useState([]);
  const [taskNotesLoading, setTaskNotesLoading] = useState(false);
  const [notesOpen, setNotesOpen]     = useState(false);
  const [notesFetched, setNotesFetched] = useState(false);
  const accountNotesFetchedRef        = useRef(false);
  const [saving, setSaving]           = useState(false);
  const [completing, setCompleting]   = useState(false);
  const [customerModalName, setCustomerModalName] = useState(null);
  const [showDateWarning, setShowDateWarning] = useState(false);
  const [warningDate, setWarningDate] = useState('');
  const [showReactivateWarning, setShowReactivateWarning] = useState(false);
  const [reactivateDate, setReactivateDate] = useState('');
  const [stageOpen, setStageOpen]     = useState(false);
  const stageRef                      = useRef(null);

  const [wasThisACall, setWasThisACall] = useState(false);
  const [callBu, setCallBu]             = useState('');
  const [callSaving, setCallSaving]     = useState(false);

  const [linkedProject, setLinkedProject] = useState(null);
  const [showProjectLinkModal, setShowProjectLinkModal] = useState(false);

  useEffect(() => {
    if (!stageOpen) return;
    const close = (e) => { if (stageRef.current && !stageRef.current.contains(e.target)) setStageOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [stageOpen]);

  const rawName = task.account_name
    || (task.account?.module?.api_name === 'Accounts' ? task.account?.name : null)
    || '';
  const fallbackCustomerName = rawName.replace(/\s*-\s*\d{4,}$/, '').trim() || 'Customer';
  const isQuoteTask = task.account?.module?.api_name === 'Quotes'
    || task.account?.module?.api_name === 'Quote'
    || task.account?.module === 'Quotes'
    || task.account?.module === 'Quote'
    || (task.account?.name && (task.account.name.includes(' · Q-') || task.account.name.includes(' - Q-')))
    || (task.subject && task.subject.toLowerCase().includes('on quote ref'));
  const quoteId    = isQuoteTask ? task.account?.id : null;
  const taskCrmUrl = task.crm_url || `https://crm.zoho.eu/crm/encon/tab/Tasks/${task.id}`;
  const customerName = quote?.account_name || fallbackCustomerName;

  useEffect(() => {
    if (!quoteId) return;
    setQuoteLoading(true);
    api.get(`${API}/zoho/quotes/${quoteId}/details`)
      .then(r => { setQuote(r.data); setQuoteStage(r.data.stage || ''); })
      .catch(() => {})
      .finally(() => setQuoteLoading(false));
  }, [quoteId]);

  useEffect(() => {
    api.get(`${API}/project-task-links/by-task/${task.id}`)
      .then(r => { if (r.data.link) setLinkedProject(r.data.link); })
      .catch(() => {});
  }, [task.id]);

  useEffect(() => {
    setCrmUsersLoading(true);
    api.get(`${API}/zoho/crm-users`)
      .then(r => setCrmUsers(r.data.users || []))
      .catch(() => {})
      .finally(() => setCrmUsersLoading(false));
  }, []);

  const loadNotes = useCallback(() => {
    if (notesFetched) return;
    setTaskNotesLoading(true);
    const params = new URLSearchParams();
    if (quoteId) params.set('quote_id', quoteId);
    if (isQuoteTask && quote?.account_id) params.set('account_id', quote.account_id);
    const qs = params.toString() ? `?${params.toString()}` : '';
    api.get(`${API}/zoho/tasks/${task.id}/notes${qs}`)
      .then(r => {
        setTaskNotes(r.data.notes || []);
        setNotesFetched(true);
        if (isQuoteTask && quote?.account_id) accountNotesFetchedRef.current = true;
      })
      .catch(() => setNotesFetched(true))
      .finally(() => setTaskNotesLoading(false));
  }, [task.id, quoteId, notesFetched, quote?.account_id, isQuoteTask]); // eslint-disable-line

  useEffect(() => {
    if (!notesOpen || !isQuoteTask || !quote?.account_id || accountNotesFetchedRef.current) return;
    accountNotesFetchedRef.current = true;
    setTaskNotesLoading(true);
    const params = new URLSearchParams();
    params.set('account_id', quote.account_id);
    api.get(`${API}/zoho/tasks/${task.id}/notes?${params.toString()}`)
      .then(r => {
        const incoming = r.data.notes || [];
        setTaskNotes(prev => {
          const existingIds = new Set(prev.map(n => n.id));
          const toAdd = incoming.filter(n => n.id && !existingIds.has(n.id));
          if (!toAdd.length) return prev;
          return [...prev, ...toAdd].sort((a, b) =>
            (b.created_at || '').localeCompare(a.created_at || ''));
        });
      })
      .catch(() => {})
      .finally(() => setTaskNotesLoading(false));
  }, [notesOpen, quote?.account_id, isQuoteTask]); // eslint-disable-line

  const toggleNotes = () => {
    setNotesOpen(v => !v);
    if (!notesFetched) loadNotes();
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    try {
      const dt = new Date(d);
      return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getFullYear()).slice(-2)}`;
    } catch { return d; }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await api.get(`${API}/zoho/quotes/${quoteId}/pdf-download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error('Failed to open PDF');
    }
  };

  const doSave = async (overrideDueDate) => {
    setSaving(true);
    const saveDueDate = overrideDueDate || dueDate;
    try {
      await api.put(`${API}/zoho/tasks/${task.id}`, {
        subject: task.subject || 'Task',
        due_date: saveDueDate,
        description: newNote.trim() || task.description || '',
        status: task.status || 'Not Started',
        priority: task.priority || 'Normal',
        owner_zoho_id: selectedOwnerId || undefined,
      });

      if (quoteId && quoteStage && quoteStage !== (quote?.stage || '')) {
        await api.put(`${API}/zoho/quotes/${quoteId}/stage`, { stage: quoteStage });
      }

      if (linkedProject?.project_id && newNote.trim()) {
        api.post(`${API}/projects/${linkedProject.project_id}/notes`, {
          content: `[Task: ${task.subject || 'Task'}] ${newNote.trim()}`,
        }).catch(() => {});
      }

      if (wasThisACall && callBu) {
        setCallSaving(true);
        try {
          const accountId = isQuoteTask ? (quote?.account_id || null) : (task.account?.id || null);
          if (accountId) {
            const callRes = await api.post(`${API}/zoho/create-call`, {
              account_id: accountId,
              contact_id: quote?.contact_id || null,
              subject: task.subject || 'Call',
              description: newNote.trim() || task.description || '',
              call_type: 'Outbound',
              call_duration_minutes: 5,
            });
            const callId = callRes?.data?.call_id || callRes?.data?.id;
            if (callId) {
              await api.post(`${API}/zoho/call-metadata`, {
                zoho_call_id: callId,
                business_unit: callBu,
                direction: 'outgoing',
              });
            }
            if (quoteId && newNote.trim()) {
              api.post(`${API}/zoho/record-note`, {
                module: 'Quotes',
                record_id: quoteId,
                note_title: `Call: ${task.subject || 'Follow Up'}`,
                note_content: newNote.trim(),
              }).catch(() => {});
            }
            toast.success('Task saved + call logged');
          } else {
            toast.warning('Task saved — no account linked for call');
          }
        } catch {
          toast.warning('Task saved but call logging failed');
        } finally {
          setCallSaving(false);
        }
      }

      if (quoteId && AUTO_CLOSE_STAGES.includes(quoteStage)) {
        await api.post(`${API}/zoho/tasks/${task.id}/complete`);
        if (!wasThisACall) toast.success('Task saved and closed');
        else if (wasThisACall && !callBu) toast.warning('Task closed — select a Business Unit to log the call next time');
        onComplete(task.id);
        return;
      }

      if (!wasThisACall) toast.success('Task saved');
      else if (wasThisACall && !callBu) toast.warning('Task saved — select a Business Unit to log the call');
      onClose();
    } catch (e) {
      toast.error('Failed to save task');
    } finally {
      setSaving(false);
      setShowDateWarning(false);
    }
  };

  const handleUpdateFollowUp = () => {
    const base = dueDate || todayStr;
    const nextDay = new Date(base);
    nextDay.setDate(nextDay.getDate() + 1);
    setWarningDate(nextDay.toISOString().slice(0, 10));
    setShowDateWarning(true);
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      if (wasThisACall && callBu) {
        setCallSaving(true);
        try {
          if (newNote.trim()) {
            await api.put(`${API}/zoho/tasks/${task.id}`, {
              subject: task.subject || 'Task',
              due_date: dueDate,
              description: newNote.trim(),
              status: task.status || 'Not Started',
              priority: task.priority || 'Normal',
              owner_zoho_id: selectedOwnerId || undefined,
            });
          }
          const accountId = isQuoteTask ? (quote?.account_id || null) : (task.account?.id || null);
          if (accountId) {
            const callRes = await api.post(`${API}/zoho/create-call`, {
              account_id: accountId,
              contact_id: quote?.contact_id || null,
              subject: task.subject || 'Call',
              description: newNote.trim() || task.description || '',
              call_type: 'Outbound',
              call_duration_minutes: 5,
            });
            const callId = callRes?.data?.id || callRes?.data?.call_id;
            if (callId) {
              await api.post(`${API}/zoho/call-metadata`, {
                zoho_call_id: callId,
                business_unit: callBu,
                direction: 'outgoing',
              });
            }
            if (quoteId && newNote.trim()) {
              api.post(`${API}/zoho/record-note`, {
                module: 'Quotes',
                record_id: quoteId,
                note_title: `Call: ${task.subject || 'Follow Up'}`,
                note_content: newNote.trim(),
              }).catch(() => {});
            }
            toast.success('Call logged + task closed');
          } else {
            toast.warning('No account linked — closing task without call');
          }
        } catch {
          toast.warning('Call logging failed — closing task anyway');
        } finally {
          setCallSaving(false);
        }
      }

      await api.post(`${API}/zoho/tasks/${task.id}/complete`);
      if (!wasThisACall) toast.success('Task closed');
      onComplete(task.id);
    } catch {
      toast.error('Failed to close task');
    } finally {
      setCompleting(false);
    }
  };

  const handleSaveCompleted = async () => {
    setSaving(true);
    try {
      if (quoteId && quoteStage && quoteStage !== (quote?.stage || '')) {
        await api.put(`${API}/zoho/quotes/${quoteId}/stage`, { stage: quoteStage });
      }
      if (newNote.trim()) {
        await api.put(`${API}/zoho/tasks/${task.id}`, {
          subject: task.subject || 'Task',
          due_date: task.due_date || dueDate,
          description: newNote.trim(),
          status: 'Completed',
          priority: task.priority || 'Normal',
          owner_zoho_id: selectedOwnerId || undefined,
        });
      }
      toast.success('Saved');
      onClose();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const doReactivate = async (newDueDate) => {
    setCompleting(true);
    try {
      await api.put(`${API}/zoho/tasks/${task.id}`, {
        subject: task.subject || 'Task',
        due_date: newDueDate,
        description: newNote.trim() || task.description || '',
        status: 'Not Started',
        priority: task.priority || 'Normal',
        owner_zoho_id: selectedOwnerId || undefined,
      });
      if (quoteId && quoteStage && quoteStage !== (quote?.stage || '')) {
        await api.put(`${API}/zoho/quotes/${quoteId}/stage`, { stage: quoteStage });
      }
      toast.success('Task reactivated');
      onReactivated && onReactivated(task.id);
    } catch {
      toast.error('Failed to reactivate task');
    } finally {
      setCompleting(false);
      setShowReactivateWarning(false);
    }
  };

  return (
    <>
      <div
        className="fixed left-0 right-0 bottom-0 flex items-center justify-center"
        style={modalUiV2 ? { top: activityBar.headerHeight, zIndex: 45, background: 'rgba(0,0,0,0.5)' } : { top: 0, zIndex: 10002, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}
        onClick={onClose}
        data-testid="task-details-modal-overlay"
      >
        <div
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
          onClick={e => e.stopPropagation()}
          data-testid="task-details-modal"
        >
          <div className="px-5 py-4 border-b border-gray-100 flex items-start gap-3">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${isCompleted ? 'bg-green-100' : 'bg-blue-100'}`}>
              {isCompleted
                ? <CheckCircle className="w-4 h-4 text-green-600" />
                : <CheckSquare className="w-4 h-4 text-blue-600" />
              }
            </div>
            <div className="flex-1 min-w-0">
              <button
                type="button"
                onClick={() => setCustomerModalName(customerName)}
                className="text-base font-bold text-gray-900 hover:text-blue-600 hover:underline text-left w-full truncate transition-colors leading-tight block"
                data-testid="task-modal-customer-link"
                title="Open Customer Manager"
              >
                {customerName}
              </button>
              <p className="text-xs text-gray-500 truncate leading-tight mt-0.5 flex items-center gap-1">
                <span className="truncate">{task.subject || 'Task'}</span>
                {task.subject && (
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(task.subject); toast.success('Subject copied'); }}
                    className="shrink-0 p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    title="Copy subject"
                    data-testid="task-subject-copy-btn"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                )}
              </p>
              <a
                href={taskCrmUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 text-[11px] text-blue-500 hover:text-blue-700 hover:underline mt-0.5 transition-colors"
                data-testid="task-crm-link"
                onClick={e => e.stopPropagation()}
              >
                <ExternalLink className="w-3 h-3" /> View Task in CRM
              </a>
            </div>
            {isCompleted && (
              <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-green-100 text-green-700 shrink-0 mt-1">Completed</span>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 transition-colors shrink-0"
              data-testid="task-details-modal-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="px-5 py-4 space-y-3 max-h-[60vh] overflow-y-auto">

            {isQuoteTask && (
              <div className="flex items-center gap-2 flex-wrap">
                {quoteLoading ? (
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading stage…
                  </div>
                ) : (
                  <>
                    <div className="relative" ref={stageRef}>
                      <button
                        type="button"
                        onClick={() => setStageOpen(v => !v)}
                        style={{
                          backgroundColor: getStageConfig(quoteStage).bg,
                          color: getStageConfig(quoteStage).text,
                          borderColor: getStageConfig(quoteStage).border,
                        }}
                        className="border-2 rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1.5 hover:opacity-90 focus:outline-none transition-all"
                        data-testid="task-modal-quote-stage-btn"
                      >
                        {quoteStage || 'Select stage'}
                        <ChevronDown className={`w-3 h-3 transition-transform ${stageOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {stageOpen && (
                        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-20 min-w-[230px] overflow-hidden">
                          {QUOTE_STAGE_CONFIGS.map(s => (
                            <button
                              key={s.value}
                              type="button"
                              onClick={() => { setQuoteStage(s.value); setStageOpen(false); }}
                              style={{ backgroundColor: s.bg, color: s.text }}
                              className="w-full text-left px-3 py-2 text-xs font-semibold hover:opacity-80 transition-opacity border-b border-white/40 last:border-0"
                              data-testid={`stage-option-${s.value.replace(/\s+/g,'-')}`}
                            >
                              {s.value}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {quote?.has_pdf && (
                      <button
                        type="button"
                        onClick={handleDownloadPdf}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-white px-3 py-1.5 rounded-full hover:opacity-80 transition-opacity"
                        style={{ backgroundColor: enconOrange }}
                        data-testid="task-modal-pdf-btn"
                        title="View Quote PDF"
                      >
                        <FileText className="w-3 h-3" /> PDF
                      </button>
                    )}
                    {(quote?.contact_name || quote?.display_phone || quote?.display_email) && (
                      <div className="flex items-center gap-2 flex-wrap mt-1">
                        {quote?.contact_name && (
                          <span className="text-xs font-semibold text-gray-700">{quote.contact_name}</span>
                        )}
                        {quote?.display_phone && (
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(quote.display_phone);
                              toast.success('Phone number copied');
                            }}
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded-lg transition-colors"
                            title="Click to copy"
                            data-testid="task-modal-contact-phone"
                          >
                            <Phone className="w-3 h-3" />
                            {quote.display_phone}
                            <Copy className="w-2.5 h-2.5 opacity-50" />
                          </button>
                        )}
                        {quote?.display_email && (
                          <a
                            href={`mailto:${quote.display_email}`}
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded-lg transition-colors"
                            title="Click to send email"
                            data-testid="task-modal-contact-email"
                            onClick={e => e.stopPropagation()}
                          >
                            <Mail className="w-3 h-3" />
                            {quote.display_email}
                          </a>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              {task.status && !isQuoteTask && (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${
                  isCompleted ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                }`}>
                  {task.status}
                </span>
              )}
              {task.priority && (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${
                  task.priority === 'High' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {task.priority}
                </span>
              )}
              {task.due_date && (
                <span className="text-[10px] text-gray-500 flex items-center gap-1">
                  <CalendarDays className="w-3 h-3" /> Due: {fmtDate(task.due_date)}
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <User className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span className="text-xs text-gray-500 shrink-0 font-medium">Owner</span>
              {crmUsersLoading ? (
                <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
              ) : (
                <select
                  value={selectedOwnerId}
                  onChange={e => setSelectedOwnerId(e.target.value)}
                  className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                  data-testid="task-modal-owner-select"
                >
                  {!selectedOwnerId && <option value="">— Select owner —</option>}
                  {crmUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              )}
            </div>

            {(isQuoteTask ? quote?.description : task.description) && (
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
                <div className="w-full border border-gray-100 rounded-lg px-3 py-2 text-sm text-gray-700 bg-gray-50 leading-relaxed max-h-28 overflow-y-auto whitespace-pre-wrap">
                  {isQuoteTask ? quote?.description : task.description}
                </div>
              </div>
            )}

            {linkedProject && (
              <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-xl px-3 py-2">
                <ClipboardList className="w-3.5 h-3.5 text-orange-500 shrink-0" />
                <div className="min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-orange-600">Linked Project</span>
                  <p className="text-xs font-semibold text-gray-800 truncate">{linkedProject.project_name}</p>
                </div>
                {linkedProject.project_stage && (
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 shrink-0 whitespace-nowrap">{linkedProject.project_stage}</span>
                )}
                <button
                  type="button"
                  onClick={() => setShowProjectLinkModal(true)}
                  className="ml-auto text-[10px] font-semibold text-orange-700 hover:text-orange-900 hover:underline shrink-0 whitespace-nowrap"
                  data-testid="task-modal-change-project-btn"
                >
                  Change
                </button>
              </div>
            )}
            {!linkedProject && (
              <button
                type="button"
                onClick={() => setShowProjectLinkModal(true)}
                className="flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-800 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-full px-3 py-1.5 w-fit transition-colors"
                data-testid="task-modal-add-to-project-btn"
              >
                <FolderKanban className="w-3.5 h-3.5" /> Add to Project
              </button>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Add Notes</label>
              <textarea
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                rows={3}
                placeholder="Add notes…"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                data-testid="task-notes-input"
              />
            </div>

            <div className="border border-gray-100 rounded-xl overflow-hidden">
              <label className="flex items-center gap-2.5 px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={wasThisACall}
                  onChange={e => setWasThisACall(e.target.checked)}
                  className="w-3.5 h-3.5 rounded accent-orange-500"
                  data-testid="was-this-a-call-checkbox"
                />
                <Phone className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-xs font-semibold text-gray-700">Was this a call?</span>
              </label>
              {wasThisACall && (
                <div className="border-t border-gray-100 px-3 pb-3 pt-2 bg-blue-50/40">
                  <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Confirm Business Unit</label>
                  <select
                    value={callBu}
                    onChange={e => setCallBu(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                    data-testid="call-bu-select"
                  >
                    <option value="">— Select Business Unit —</option>
                    {['Structural', 'Merchant', 'Industrial'].map(bu => (
                      <option key={bu} value={bu}>{bu}</option>
                    ))}
                  </select>
                  {callSaving && (
                    <p className="text-[10px] text-blue-500 flex items-center gap-1 mt-1.5">
                      <Loader2 className="w-3 h-3 animate-spin" /> Logging call…
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="border border-gray-100 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={toggleNotes}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                data-testid="task-prev-notes-toggle"
              >
                <span className="flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5 text-gray-400" />
                  Previous Notes
                  {notesFetched && taskNotes.length > 0 && (
                    <span className="bg-gray-200 text-gray-600 rounded-full px-1.5 py-0.5 text-[10px] font-bold">{taskNotes.length}</span>
                  )}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${notesOpen ? 'rotate-180' : ''}`} />
              </button>
              {notesOpen && (
                <div className="border-t border-gray-100 max-h-52 overflow-y-auto">
                  {taskNotesLoading ? (
                    <div className="flex items-center gap-2 p-3 text-xs text-gray-400">
                      <Loader2 className="w-3 h-3 animate-spin" /> Loading notes…
                    </div>
                  ) : taskNotes.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-gray-400">No previous notes found.</p>
                  ) : (
                    taskNotes.map(n => (
                      <div key={n.id} className="px-3 py-2 border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-[10px] font-semibold text-gray-500">{n.author || 'Unknown'}</span>
                          <span className="text-[10px] text-gray-400">{n.created_at ? fmtDate(n.created_at) : ''}</span>
                        </div>
                        {n.title && <p className="text-[11px] font-medium text-gray-700 mb-0.5">{n.title}</p>}
                        <p className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed">{n.content}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {isCompleted ? (
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-gray-100 bg-gray-50">
              <button
                type="button"
                onClick={handleSaveCompleted}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                data-testid="task-modal-save-completed-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  const nextDay = new Date();
                  nextDay.setDate(nextDay.getDate() + 1);
                  setReactivateDate(nextDay.toISOString().slice(0, 10));
                  setShowReactivateWarning(true);
                }}
                disabled={completing}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors disabled:opacity-50"
                data-testid="task-modal-reactivate-btn"
              >
                {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Update follow up date &amp; reactivate
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-gray-100 bg-gray-50">
              <button
                type="button"
                onClick={handleComplete}
                disabled={completing}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors disabled:opacity-50"
                data-testid="task-modal-complete-btn"
              >
                {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Close Task
              </button>
              <button
                type="button"
                onClick={handleUpdateFollowUp}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold transition-colors disabled:opacity-50"
                style={{ backgroundColor: enconOrange }}
                data-testid="task-modal-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarDays className="w-4 h-4" />}
                Update follow up date &amp; save
              </button>
            </div>
          )}

          {showDateWarning && (
            <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center rounded-2xl z-10 p-6">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-5 w-full max-w-xs text-center">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-3">
                  <CalendarDays className="w-5 h-5 text-orange-500" />
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-1">Set Follow-up Date</h3>
                <p className="text-xs text-gray-500 mb-3 leading-relaxed">
                  Choose a new follow-up date for this task.
                </p>
                <input
                  type="date"
                  value={warningDate}
                  min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                  onChange={e => setWarningDate(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 mb-3"
                  data-testid="task-date-warning-input"
                />
                <div className="flex gap-2 justify-center">
                  <button
                    type="button"
                    onClick={() => setShowDateWarning(false)}
                    className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    Go Back
                  </button>
                  <button
                    type="button"
                    onClick={() => { setDueDate(warningDate); doSave(warningDate); }}
                    disabled={!warningDate || warningDate <= todayStr || saving}
                    className="px-4 py-1.5 text-sm text-white rounded-lg disabled:opacity-40 transition-opacity hover:opacity-90"
                    style={{ backgroundColor: enconOrange }}
                    data-testid="task-date-warning-confirm"
                  >
                    {saving ? 'Saving…' : 'Save with New Date'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showReactivateWarning && (
            <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center rounded-2xl z-10 p-6">
              <div className="bg-white rounded-2xl shadow-xl border border-green-200 p-5 w-full max-w-xs text-center">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <CalendarDays className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-1">Set New Follow-up Date</h3>
                <p className="text-xs text-gray-500 mb-3 leading-relaxed">
                  Choose a follow-up date to reactivate this task.
                </p>
                <input
                  type="date"
                  value={reactivateDate}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={e => setReactivateDate(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 mb-3"
                  data-testid="task-reactivate-date-input"
                />
                <div className="flex gap-2 justify-center">
                  <button
                    type="button"
                    onClick={() => setShowReactivateWarning(false)}
                    className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    Go Back
                  </button>
                  <button
                    type="button"
                    onClick={() => doReactivate(reactivateDate)}
                    disabled={!reactivateDate || completing}
                    className="px-4 py-1.5 text-sm text-white rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-40 transition-colors"
                    data-testid="task-reactivate-confirm"
                  >
                    {completing ? 'Reactivating…' : 'Reactivate Task'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {customerModalName && (
        <CustomerManagerModal
          searchName={customerModalName}
          onClose={() => setCustomerModalName(null)}
          autoSelectFirst
        />
      )}

      <TaskProjectLinkModal
        isOpen={showProjectLinkModal}
        onClose={() => setShowProjectLinkModal(false)}
        taskId={task.id}
        currentProject={linkedProject}
        onLinked={(link) => setLinkedProject(link)}
      />
    </>
  );
};

const CustomerManagerModal = ({ searchName, onClose, autoSelectFirst = false }) => (
  <ContactNotesTasksModal
    isOpen={true}
    onClose={onClose}
    initialSearch={searchName}
  />
);

export default NotificationBars;
